import React, { useEffect, useMemo, useState } from 'react';

/**
 * 🖋️ Killer mad-lib (KILLER_MODE spec §1, Nina 07-19: FULL authorship) — the
 * killer fills 3 blanks in ~30s; the picks become the case's ground truth
 * (backend seeds every clue/witness from them). Tap-to-pick chips only, no
 * free text — the server validates against the same pools it sent.
 *
 * Renders ABOVE the cold-open reveal (z 300001): dramatically it's the last
 * page of "YOU ARE THE KILLER". Timeout = backend authors fallback picks, so
 * expiry here just closes quietly.
 */
const BLANK = '___';

const KillerMadlibModal = ({ madlibData, expiresAt, onSubmit, onExpire, theme = {} }) => {
  const [picks, setPicks] = useState({ weapon: null, place: null, left_behind: null });
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 1000));
  });

  useEffect(() => {
    const t = setInterval(() => {
      const ms = new Date(expiresAt).getTime() - Date.now();
      const s = Math.max(0, Math.ceil(ms / 1000));
      setSecondsLeft(s);
      if (s <= 0) {
        clearInterval(t);
        if (onExpire) onExpire();
      }
    }, 250);
    return () => clearInterval(t);
  }, [expiresAt, onExpire]);

  const line = madlibData?.line || `I did it with ${BLANK} in ${BLANK}, and like an idiot I left ${BLANK} behind.`;
  const groups = useMemo(() => ([
    { key: 'weapon', label: 'THE HOW', options: madlibData?.weapons || [] },
    { key: 'place', label: 'THE WHERE', options: madlibData?.places || [] },
    { key: 'left_behind', label: 'THE MISTAKE', options: madlibData?.left_behind || [] },
  ]), [madlibData]);

  // The line with current picks filled in live — watching your confession
  // assemble itself is the dread.
  const filledLine = useMemo(() => {
    const order = [picks.weapon, picks.place, picks.left_behind];
    let i = 0;
    return line.split(BLANK).reduce((acc, part, idx, arr) => {
      acc.push(<span key={`t-${idx}`}>{part}</span>);
      if (idx < arr.length - 1) {
        const val = order[i];
        i += 1;
        acc.push(
          <span key={`b-${idx}`} style={{
            color: val ? '#fca5a5' : 'rgba(255,255,255,0.3)',
            borderBottom: '2px solid rgba(252,165,165,0.5)',
            fontStyle: val ? 'italic' : 'normal',
            padding: '0 3px',
          }}>{val || '______'}</span>
        );
      }
      return acc;
    }, []);
  }, [line, picks]);

  const allPicked = picks.weapon && picks.place && picks.left_behind;

  const submit = async () => {
    if (!allPicked || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({ weapon: picks.weapon, place: picks.place, left_behind: picks.left_behind });
    } catch (e) {
      setSubmitting(false);
    }
  };

  const accent = '#dc2626';
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300001,
      background: 'rgba(4,2,8,0.92)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      overflowY: 'auto',
    }}>
      <div style={{
        width: '100%', maxWidth: 560, borderRadius: 16,
        border: `1px solid ${accent}55`,
        background: 'linear-gradient(180deg, rgba(40,8,10,0.65), rgba(10,6,14,0.9))',
        boxShadow: `0 12px 48px rgba(0,0,0,0.7), 0 0 40px ${accent}22`,
        padding: '22px 20px', color: '#fff', textAlign: 'center',
      }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '2.5px', color: '#fca5a5', textTransform: 'uppercase' }}>
          🖋️ Your crime. Your words.
        </div>
        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', marginTop: 6 }}>
          How'd you do it? You can tell us.
        </div>

        {/* Countdown bar — 30s, drains red (server window is wider; the bar
            normalizes to the UI's advertised 30) */}
        <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.12)', margin: '12px 0 16px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 2,
            width: `${Math.min(100, Math.max(0, (secondsLeft / 30) * 100))}%`,
            background: `linear-gradient(90deg, ${accent}, #f87171)`,
            transition: 'width 0.25s linear',
          }} />
        </div>

        <div style={{
          fontSize: '1.05rem', lineHeight: 1.7, fontStyle: 'italic',
          fontFamily: 'Georgia, serif', color: 'rgba(255,255,255,0.92)',
          padding: '0 4px', marginBottom: 16, textAlign: 'left',
        }}>
          "{filledLine}"
        </div>

        {groups.map(g => (
          <div key={g.key} style={{ marginBottom: 12, textAlign: 'left' }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '1.8px', color: 'rgba(252,165,165,0.8)', marginBottom: 6 }}>
              {g.label}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {g.options.map(opt => {
                const active = picks[g.key] === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setPicks(p => ({ ...p, [g.key]: active ? null : opt }))}
                    style={{
                      padding: '8px 12px', borderRadius: 999, cursor: 'pointer',
                      fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit',
                      border: active ? `1.5px solid ${accent}` : '1px solid rgba(255,255,255,0.2)',
                      background: active ? 'rgba(220,38,38,0.28)' : 'rgba(255,255,255,0.05)',
                      color: active ? '#fecaca' : 'rgba(255,255,255,0.85)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={submit}
          disabled={!allPicked || submitting}
          style={{
            marginTop: 8, width: '100%', padding: '13px 20px', borderRadius: 12,
            border: 'none', cursor: allPicked && !submitting ? 'pointer' : 'default',
            background: allPicked ? `linear-gradient(90deg, ${accent}, #991b1b)` : 'rgba(255,255,255,0.08)',
            color: allPicked ? '#fff' : 'rgba(255,255,255,0.35)',
            fontSize: '0.95rem', fontWeight: 800, letterSpacing: '1px',
            fontFamily: 'inherit',
          }}
        >
          {submitting ? 'SEALING...' : allPicked ? '🖋️ SIGN IT' : 'PICK ALL THREE'}
        </button>
        <div style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
          {secondsLeft}s. Hesitate and the night writes itself.
        </div>
      </div>
    </div>
  );
};

export default KillerMadlibModal;
